'use client';
import { ServerError } from '@azure/msal-browser';
import { Avatar, Box, Button, Icon, Menu, Portal } from '@chakra-ui/react';
import NextLink from 'next/link';
import NProgress from 'nprogress';
import { forwardRef, useEffect, useState } from 'react';
import { FaChartLine, FaSignOutAlt, FaTable } from 'react-icons/fa';
import { FaRankingStar } from 'react-icons/fa6';
import { Loading } from '../components/loading';
import { appInsights } from '../lib/application-insights/client';
import '../lib/client-polyfills';
import { getUserGamertag } from '../lib/clients';
import {
  useCurrentUser,
  useCurrentUserGamertag,
} from '../lib/hooks/current-user';
import {
  clearCache,
  getAllAccounts,
  logout,
} from '../lib/msal-instances/client';

const LoadingMenuButton = forwardRef(function LoadingMenuButton(props, ref) {
  return (
    <Box ref={ref} h="40px">
      <Loading {...props} />
    </Box>
  );
});

export function CurrentUserHeader() {
  const gamertag = useCurrentUserGamertag();
  const currentUser = useCurrentUser();
  const [localUserCheckCompleted, setLocalUserChecked] = useState(false);
  const [gamerpicLoaded, setGamerpicLoaded] = useState(false);
  useEffect(() => {
    if (gamertag) {
      appInsights.setAuthenticatedUserContext(
        gamertag.replace(/[,;=| ]+/g, '_')
      );
      setLocalUserChecked(true);
    } else {
      appInsights.clearAuthenticatedUserContext();
    }
  }, [gamertag]);

  useEffect(() => {
    getAllAccounts()
      .then(async ({ length }) => {
        if (length > 0) {
          // Load current user into cache
          await getUserGamertag();
        }
      })
      .catch((e) => {
        if (e instanceof ServerError) {
          clearCache().then(() => {
            window.dispatchEvent(
              new CustomEvent('interaction-required', { detail: e })
            );
          });
        }
        throw e;
      })
      .finally(() => {
        setLocalUserChecked(true);
      });
  }, []);

  return (
    <Box>
      {localUserCheckCompleted ? (
        gamertag == null ? (
          <Avatar.Root
            size="md"
            cursor="pointer"
            onClick={async () => {
              const { length } = await getAllAccounts();
              if (length === 0) {
                window.dispatchEvent(
                  new CustomEvent('interaction-required', {
                    detail: true,
                  })
                );
              } else {
                getUserGamertag();
              }
            }}
          >
            <Avatar.Fallback />
            <Avatar.Image />
          </Avatar.Root>
        ) : (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button variant="plain" px={0}>
                <Avatar.Root
                  size="md"
                  backgroundColor={gamerpicLoaded ? 'transparent' : undefined}
                >
                  <Avatar.Fallback name={gamertag} />
                  <Avatar.Image
                    src={currentUser?.gamerpic.small}
                    onLoad={() => {
                      setGamerpicLoaded(true);
                    }}
                  />
                </Avatar.Root>
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item asChild value={'matches'} cursor={'pointer'}>
                    <NextLink href={'/matches?gamertag=' + gamertag}>
                      <Icon as={FaTable} />
                      Matches
                    </NextLink>
                  </Menu.Item>
                  <Menu.Item asChild value="profile" cursor={'pointer'}>
                    <NextLink href={'/players/' + gamertag}>
                      <Icon as={FaChartLine} />
                      Profile
                    </NextLink>
                  </Menu.Item>
                  <Menu.Item asChild value="leaderboard" cursor={'pointer'}>
                    <NextLink href={'/leaderboard?gamertag=' + gamertag}>
                      <Icon as={FaRankingStar} />
                      Leaderboard
                    </NextLink>
                  </Menu.Item>
                  <Menu.Separator />
                  <Menu.Item
                    value="logout"
                    onClick={() => {
                      NProgress.start();
                      return logout();
                    }}
                    cursor={'pointer'}
                  >
                    <Icon as={FaSignOutAlt} />
                    Logout
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        )
      ) : (
        <Loading />
      )}
    </Box>
  );
}
